import { useQueryClient } from '@tanstack/react-query';

import {
  AiKnowledgeBaseEntriesFiltersDto,
  checkAiKnowledgeBaseCoverage,
  createAiKnowledgeBaseImport,
  createAiKnowledgeBaseEntry,
  CreateAiKnowledgeBaseImportRequestDto,
  CreateAiKnowledgeBaseEntryRequestDto,
  deleteAiKnowledgeBaseEntry,
  getAiKnowledgeBaseEntries,
  getAiKnowledgeBaseUsage,
  updateAiKnowledgeBaseEntry,
  UpdateAiKnowledgeBaseEntryRequestDto,
} from '@/shared/api/ai-knowledge-base';
import { useAppMutation } from '@/shared/lib/useAppMutation';
import { useAppQuery } from '@/shared/lib/useAppQuery';

const queryKey = (companyId: string) => ['ai-knowledge-base', companyId] as const;

const entriesQueryKey = (
  companyId: string,
  filters: AiKnowledgeBaseEntriesFiltersDto
) => [...queryKey(companyId), filters] as const;

const usageQueryKey = (companyId: string, id: string) =>
  [...queryKey(companyId), 'usage', id] as const;

export function useAiKnowledgeBaseEntries(
  companyId: string,
  filters: AiKnowledgeBaseEntriesFiltersDto = {},
  enabled = true
) {
  return useAppQuery({
    queryKey: entriesQueryKey(companyId, filters),
    queryFn: () => getAiKnowledgeBaseEntries(companyId, filters),
    enabled,
    customError: true,
  });
}

export function useAiKnowledgeBaseUsage(
  companyId: string,
  id: string,
  enabled = true
) {
  return useAppQuery({
    queryKey: usageQueryKey(companyId, id),
    queryFn: () => getAiKnowledgeBaseUsage(companyId, id),
    enabled: enabled && Boolean(companyId && id),
    customError: true,
  });
}

export function useAiKnowledgeBaseCoverage(companyId: string) {
  return useAppMutation({
    mutationFn: (questions: string[]) =>
      checkAiKnowledgeBaseCoverage(companyId, questions),
    customError: true,
  });
}

export function useCreateAiKnowledgeBaseEntry(companyId: string) {
  const queryClient = useQueryClient();

  return useAppMutation({
    mutationFn: (data: CreateAiKnowledgeBaseEntryRequestDto) =>
      createAiKnowledgeBaseEntry(companyId, data),
    customError: true,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(companyId) });
    },
  });
}

export function useCreateAiKnowledgeBaseImport(companyId: string) {
  const queryClient = useQueryClient();

  return useAppMutation({
    mutationFn: (data: CreateAiKnowledgeBaseImportRequestDto) =>
      createAiKnowledgeBaseImport(companyId, data),
    customError: true,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(companyId) });
    },
  });
}

export function useUpdateAiKnowledgeBaseEntry(companyId: string) {
  const queryClient = useQueryClient();

  return useAppMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateAiKnowledgeBaseEntryRequestDto;
    }) => updateAiKnowledgeBaseEntry(companyId, id, data),
    customError: true,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(companyId) });
    },
  });
}

export function useDeleteAiKnowledgeBaseEntry(companyId: string) {
  const queryClient = useQueryClient();

  return useAppMutation({
    mutationFn: (id: string) => deleteAiKnowledgeBaseEntry(companyId, id),
    customError: true,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(companyId) });
    },
  });
}
